import * as React from "react";
import { cn } from "../../lib/utils";
import { getCurrencyFractionDigits, getCurrencyLocale, normalizeCurrency } from "../../lib/currency";

const getCurrencySeparators = (currency) => {
  const parts = new Intl.NumberFormat(getCurrencyLocale(currency)).formatToParts(12345.6);
  return {
    decimal: parts.find((part) => part.type === "decimal")?.value || ".",
    group: parts.find((part) => part.type === "group")?.value || ",",
  };
};

const groupInteger = (integerPart, currency) => {
  const digits = String(integerPart || "0").replace(/\D/g, "") || "0";
  return new Intl.NumberFormat(getCurrencyLocale(currency), {
    maximumFractionDigits: 0, useGrouping: true,
  }).format(Number(digits));
};

const formatCanonicalNumber = (value, currency = "COP") => {
  if (value === "" || value === null || value === undefined) return "";
  const fractionDigits = getCurrencyFractionDigits(currency);
  const canonical = String(value).replace(/[^\d.]/g, "");
  const [rawInteger = "", ...fractionParts] = canonical.split(".");
  const integerPart = rawInteger.replace(/\D/g, "");
  if (!integerPart) return "";
  if (!fractionDigits || fractionParts.length === 0) return groupInteger(integerPart, currency);
  const decimalPart = fractionParts.join("").replace(/\D/g, "").slice(0, fractionDigits);
  const { decimal } = getCurrencySeparators(currency);
  const hasTrailingDecimal = canonical.endsWith(".");
  return `${groupInteger(integerPart, currency)}${hasTrailingDecimal || decimalPart ? decimal : ""}${decimalPart}`;
};

const parseFormattedNumber = (formattedValue, currency = "COP") => {
  if (formattedValue === "" || formattedValue === null || formattedValue === undefined) return "";
  const fractionDigits = getCurrencyFractionDigits(currency);
  const raw = String(formattedValue).replace(/[^\d.,]/g, "");
  if (!fractionDigits) return raw.replace(/\D/g, "");

  const { decimal: localeDecimal } = getCurrencySeparators(currency);
  const separatorIndexes = [...raw].reduce((indexes, char, index) => {
    if (char === "." || char === ",") indexes.push(index);
    return indexes;
  }, []);
  if (!separatorIndexes.length) return raw.replace(/\D/g, "");

  const lastIndex = separatorIndexes[separatorIndexes.length - 1];
  const lastSeparator = raw[lastIndex];
  const digitsAfter = raw.slice(lastIndex + 1).replace(/\D/g, "");
  const hasBothSeparators = raw.includes(".") && raw.includes(",");
  const separatorCount = raw.split(lastSeparator).length - 1;
  const isDecimal = hasBothSeparators
    || (separatorCount === 1 && (lastSeparator === localeDecimal || digitsAfter.length <= fractionDigits));

  if (!isDecimal) return raw.replace(/\D/g, "");
  const integerPart = raw.slice(0, lastIndex).replace(/\D/g, "") || "0";
  const decimalPart = digitsAfter.slice(0, fractionDigits);
  return `${integerPart}.${decimalPart}`;
};

const formatNumberWithSeparators = (value, currency = "COP") => {
  const canonical = parseFormattedNumber(value, currency);
  return formatCanonicalNumber(canonical, currency);
};

const getCurrencySymbol = (currency) => {
  const code = normalizeCurrency(currency);
  try {
    return new Intl.NumberFormat(getCurrencyLocale(code), {
      style: "currency", currency: code, currencyDisplay: "narrowSymbol",
    }).formatToParts(0).find((part) => part.type === "currency")?.value || code;
  } catch {
    return code;
  }
};

const CurrencyInput = React.forwardRef(
  ({ className, currency = "COP", value, onChange, placeholder, ...props }, ref) => {
    const [displayValue, setDisplayValue] = React.useState("");
    const fractionDigits = getCurrencyFractionDigits(currency);

    React.useEffect(() => {
      setDisplayValue(formatCanonicalNumber(value, currency));
    }, [value, currency]);

    const handleChange = (event) => {
      const canonicalValue = parseFormattedNumber(event.target.value, currency);
      setDisplayValue(formatCanonicalNumber(canonicalValue, currency));
      onChange?.({ target: { value: canonicalValue, name: event.target.name } });
    };

    return (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 text-sm pointer-events-none">
          {getCurrencySymbol(currency)}
        </span>
        <input
          type="text"
          inputMode={fractionDigits ? "decimal" : "numeric"}
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background pl-8 pr-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          ref={ref}
          value={displayValue}
          onChange={handleChange}
          placeholder={placeholder || "0"}
          {...props}
        />
      </div>
    );
  }
);

CurrencyInput.displayName = "CurrencyInput";

export { CurrencyInput, formatNumberWithSeparators, getCurrencyFractionDigits, parseFormattedNumber };
